import { Link, useParams } from 'react-router';
import { ArrowLeft, Phone, Truck } from 'lucide-react';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { DeliveryCalculator } from '@/features/delivery/DeliveryCalculator';
import { ProductImage } from '@/features/products/ProductImage';
import { PUBLIC_CATEGORY, specificationEntries, usePublicProduct } from '@/features/products/usePublicProducts';
import { useContact } from '@/features/settings/useSiteSettings';
import { formatForint } from '@/lib/utils';

const BackLink = () => (
  <Link to="/termekek" className="inline-flex min-h-11 items-center gap-2 font-semibold text-teal-ink no-underline">
    <ArrowLeft className="size-5" aria-hidden="true" />
    Minden eszköz
  </Link>
);

const RelatedCard = ({ title, name, slug, note }: { title: string; name: string; slug: string; note: string }) => (
  <div className="rounded-xl bg-sand-soft p-6">
    <p className="text-sm font-semibold text-teal-ink">{title}</p>
    <p className="mt-1 font-display text-lg font-bold text-navy">{name}</p>
    <p className="mt-1 text-sm text-ink-soft">{note}</p>
    <Link
      to={`/termekek/${slug}`}
      className="mt-4 inline-flex min-h-11 items-center rounded-md border-2 border-navy px-5 font-semibold text-navy no-underline hover:bg-navy hover:text-cream"
    >
      Megnézem
    </Link>
  </div>
);

export const ProductDetailPage = () => {
  const contact = useContact();
  const { slug } = useParams<{ slug: string }>();
  const product = usePublicProduct(slug);

  if (product.isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1220px] px-4 py-12 sm:px-6">
        <Spinner label="Termék betöltése…" />
      </div>
    );
  }

  if (product.isError || !product.data) {
    const missing = product.error instanceof ApiError && product.error.status === 404;

    return (
      <div className="mx-auto w-full max-w-[840px] px-4 py-12 sm:px-6">
        <title>Termék nem található – Ápolási betegágy bérlés</title>
        <BackLink />
        <Alert tone={missing ? 'info' : 'danger'} title={missing ? 'Ez a termék nem található' : 'A termék most nem érhető el'} className="mt-6">
          {missing
            ? 'Lehet, hogy elírás történt a címben, vagy a termék már nem bérelhető. Az összes eszköz a listában megtalálható.'
            : 'Kérjük, frissítse az oldalt, vagy hívjon minket telefonon.'}
        </Alert>
      </div>
    );
  }

  const item = product.data;
  const category = PUBLIC_CATEGORY[item.category];
  const specs = specificationEntries(item.specifications);
  const gallery = item.images.filter((url) => url !== item.imageUrl);
  const description = item.shortDescription ?? item.description ?? `${item.name} bérlése havi díjjal, kiszállítással.`;

  return (
    <div className="mx-auto w-full max-w-[1220px] px-4 py-10 sm:px-6">
      <title>{`${item.name} bérlése – ${formatForint(item.monthlyPrice)} / hó`}</title>
      <meta name="description" content={description.slice(0, 160)} />

      <BackLink />

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <div className="aspect-[4/3] overflow-hidden rounded-xl border-2 border-line bg-paper">
            <ProductImage src={item.imageUrl} alt={item.name} />
          </div>

          {gallery.length > 0 && (
            <ul className="grid list-none grid-cols-3 gap-3 p-0 sm:grid-cols-4">
              {gallery.map((url) => (
                <li key={url} className="aspect-square overflow-hidden rounded-lg border-2 border-line">
                  <img src={url} alt="" loading="lazy" className="size-full object-cover" />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <div>
            <p className="text-sm font-semibold text-teal-ink">{category.title}</p>
            <h1 className="mt-1 text-3xl">{item.name}</h1>
            {item.shortDescription && <p className="mt-3 text-lg text-ink-soft">{item.shortDescription}</p>}
          </div>

          <div className="rounded-xl bg-navy p-6 text-cream">
            <p className="text-sm text-cream/75">Bérleti díj</p>
            <p className="mt-1 font-display text-4xl font-bold text-cream">
              <span>{formatForint(item.monthlyPrice)}</span>
              <span className="ml-2 text-lg font-normal text-cream/75">/ hó</span>
            </p>
            {item.minimumRentalDays > 1 && (
              <p className="mt-2 text-sm text-cream/75">Legrövidebb bérlési idő: {item.minimumRentalDays} nap</p>
            )}

            <Link
              to="/rendeles"
              className="mt-5 inline-flex min-h-14 w-full items-center justify-center rounded-md border-2 border-teal bg-teal px-6 font-display text-lg font-bold text-navy-deep no-underline hover:bg-teal-light"
            >
              Bérlési igény leadása
            </Link>
            <a
              href={contact.phoneHref}
              className="mt-3 inline-flex min-h-12 w-full items-center justify-center gap-2.5 rounded-md border-2 border-cream/40 px-6 font-semibold text-cream no-underline hover:bg-cream/10"
            >
              <Phone className="size-5" aria-hidden="true" />
              Hívjon: {contact.phone}
            </a>
            <p className="mt-3 text-center text-sm text-cream/75">
              {contact.weekdayHours}, {contact.weekendHours}
            </p>
          </div>

          {item.description && (
            <div>
              <h2 className="text-xl">Leírás</h2>
              <p className="mt-2 whitespace-pre-line text-ink">{item.description}</p>
            </div>
          )}

          {specs.length > 0 && (
            <div>
              <h2 className="text-xl">Tulajdonságok</h2>
              <dl className="mt-3 divide-y divide-line rounded-lg border-2 border-line bg-paper">
                {specs.map(([label, value]) => (
                  <div key={label} className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] sm:gap-4">
                    <dt className="font-semibold text-navy">{label}</dt>
                    <dd className="m-0 text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {item.recommendedMattress && (
            <RelatedCard
              title="Ajánlott matrac ehhez az ágyhoz"
              name={item.recommendedMattress.name}
              slug={item.recommendedMattress.slug}
              note="Az ágyhoz méretezve, a felfekvés megelőzésére."
            />
          )}

          {item.recommendedForBeds.length > 0 && (
            <div className="rounded-xl bg-sand-soft p-6">
              <p className="text-sm font-semibold text-teal-ink">Ehhez az ágyhoz ajánljuk</p>
              <ul className="mt-2 flex list-none flex-col gap-2 p-0">
                {item.recommendedForBeds.map((bed) => (
                  <li key={bed.id}>
                    <Link to={`/termekek/${bed.slug}`} className="inline-flex min-h-11 items-center font-display text-lg font-bold text-navy">
                      {bed.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-xl border-2 border-line bg-paper p-6">
            <div className="flex gap-4">
              <Truck className="size-8 shrink-0 text-teal-ink" aria-hidden="true" />
              <div>
                <h2 className="text-lg">Szállítás és beüzemelés</h2>
                <p className="mt-1 text-sm text-ink-soft">
                  Házhoz visszük, összeszereljük és megmutatjuk a használatát. Nézze meg, mennyibe kerül a
                  kiszállítás az Ön címére – {contact.serviceArea}.
                </p>
              </div>
            </div>
            <DeliveryCalculator compact className="mt-4 border-2 border-line" />
          </div>
        </div>
      </div>
    </div>
  );
};
